import { faChevronDown, faQuestionCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext } from "react";
import { NavLink, RouteComponentProps, withRouter } from "react-router-dom";
import { ERoute } from "../../../classes/Routes";
import { AuthUser } from "../../../classes/AuthUser";
import HeaderContext from "../../../context/HeaderContext";
import Backdrop from "../../UI/Backdrop/Backdrop";
import Search from "../Search/Search";
import classes from "./Menu.module.scss";
import { useDispatch } from "react-redux";
import i18n, { ELanguage } from "../../../i18n";
import * as actions from "../../../store/actions";
import ModalContext, { EModalSize } from "../../UI/Modal/ModalContext";
import ServiceGuideModal from "../../ServiceGuideModal/ServiceGuideModal";

export enum EMenuType {
  DESKTOP,
  MOBILE,
}

interface IProps extends RouteComponentProps {
  type: EMenuType;
  isOpen: boolean;
  toggleMenu: () => void;
  currentUser: AuthUser;
}

export interface IMenuItem {
  text: string;
  link?: string;
  to?: ERoute;
  subMenuItems?: IMenuItem[];
  action?: () => void;
  search?: boolean;
}

const Menu: React.FC<IProps> = ({ type, isOpen, toggleMenu, location, currentUser }) => {
  const { menuItems, dropdownMenuItems } = useContext(HeaderContext);
  const { setModal } = useContext(ModalContext);
  const dispatch = useDispatch();

  const openServiceGuideModal = () => setModal({
    isOpen: true,
    title: "Käyttöohjeet palveluun",
    size: EModalSize.MEDIUM,
    content: <ServiceGuideModal />,
  });

  const changeLanguage = (lang: ELanguage) => {
    i18n.changeLanguage(lang);
    dispatch(actions.changeUserLanguage(lang));
  };

  const classNames = [classes.Menu];
  switch (type) {
    case EMenuType.DESKTOP:
      classNames.push(classes.DesktopMenu);
      break;
    case EMenuType.MOBILE:
      classNames.push(classes.MobileMenu);
      break;
    default:
      break;
  }

  if (isOpen) {
    classNames.push(classes.isOpen);
  }

  const menuItemClickHandler = (menuItem: IMenuItem): void => {
    if (menuItem.link) {
      window.location.href = menuItem.link;
    }
    if (menuItem.action) {
      menuItem.action();
    }
  };

  const isActiveChild = (menuItems?: IMenuItem[]): boolean => {
    if (!menuItems) return false;
    return menuItems.some(
      (menuItem) => menuItem.to && location.pathname.startsWith(menuItem.to)
    );
  };

  const renderMenuItem = (menuItem: IMenuItem, userDropdown?: boolean) => {
    if (type === EMenuType.MOBILE && menuItem.search) return null;
    const key = menuItem.text;
    let item: JSX.Element | null = null;
    if (menuItem.to) {
      if(!currentUser.routes.includes(menuItem.to)) return null;

      item = (
        <NavLink
          to={menuItem.to}
          className={classes.MenuItem}
          activeClassName={classes.MenuItemActive}
        >
          {menuItem.text}
        </NavLink>
      );
    } else {
      if (menuItem.search && !currentUser.allowSearch) return null;
      if (menuItem.subMenuItems) {
        const hasAtleastOneAccess = menuItem.subMenuItems.some(item => item.to && currentUser.routes.includes(item.to));
        if(!hasAtleastOneAccess) return null;
      }

      const classNames = [classes.MenuItem];
      const isActive = isActiveChild(menuItem.subMenuItems);
      if (isActive) {
        classNames.push(classes.MenuItemActive);
      }
      if (userDropdown) {
        classNames.push(classes.UserDropdown);
      }
      item = (
        <div
          className={classNames.join(" ")}
          onClick={() => menuItemClickHandler(menuItem)}
        >
          {menuItem.search ? (
            <Search text={menuItem.text} />
          ) : menuItem.text}{" "}
          {menuItem.subMenuItems && (
            <FontAwesomeIcon
              icon={faChevronDown}
              className={classes.MenuIcon}
            />
          )}
        </div>
      );
    }

    const containerClasses = [classes.MenuItemContainer];

    if (userDropdown) {
      containerClasses.push(classes.UserDropdownContainer);
    }
    if (menuItem.search) {
      containerClasses.push(classes.Search);
    }

    return (
      <div className={containerClasses.join(" ")} key={key}>
        {item}
        {menuItem.subMenuItems && (
          <div className={classes.Dropdown}>
            {menuItem.subMenuItems.map((menuItem) => renderMenuItem(menuItem))}
          </div>
        )}
      </div>
    );
  };

  return (
    <React.Fragment>
      <Backdrop isOpen={isOpen} onClick={toggleMenu} />
      <div className={classNames.join(" ")}>
        <div className={classes.MobileHeader}>
          <span className={classes.Toggle} onClick={toggleMenu}>
            <FontAwesomeIcon icon={faTimes} />
          </span>
        </div>
        {menuItems.map(menuItem => renderMenuItem(menuItem))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={openServiceGuideModal} title="Käyttöohjeet palveluun">
            <FontAwesomeIcon icon={faQuestionCircle} style={{ fontSize: '1.1rem', color: '#333' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ cursor: 'pointer', padding: '0 0.5rem', fontWeight: i18n.language === 'fi' ? 'bold' : 'normal', color: i18n.language === 'fi' ? '#00A650' : '#333' }} onClick={() => changeLanguage(ELanguage.FI)}>FI</div>
            <div>|</div>
            <div style={{ cursor: 'pointer', padding: '0 0.5rem', fontWeight: i18n.language === 'en' ? 'bold' : 'normal', color: i18n.language === 'en' ? '#00A650' : '#333' }} onClick={() => changeLanguage(ELanguage.EN)}>EN</div>
          </div>
        </div>
        {dropdownMenuItems.map(menuItem => renderMenuItem(menuItem, true))}
      </div>
    </React.Fragment>
  );
};

export default withRouter(Menu);
